import { useState, useRef, useEffect } from 'react';
import { sendChatMessage, getConversationHistory } from '../services/api';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
}

export const useChat = (caseId?: string) => {
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      role: 'assistant',
      content: caseId 
        ? `Hello! I'm Lex, your AI legal assistant. I can help you with questions about this case. What would you like to know?`
        : `Hello! I'm Lex, your AI legal assistant. How can I help you?`,
    },
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [conversationId, setConversationId] = useState<string | undefined>(undefined);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [isConversationsOpen, setIsConversationsOpen] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Reset chat when case context changes
  useEffect(() => {
    setMessages([
      {
        id: '1',
        role: 'assistant',
        content: caseId 
          ? `Hello! I'm Lex, your AI legal assistant. I can help you with questions about this case. What would you like to know?`
          : `Hello! I'm Lex, your AI legal assistant. How can I help you?`,
      },
    ]);
    setConversationId(undefined); // Start a new conversation for the new case
  }, [caseId]);

  const handleSend = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: input,
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      const response = await sendChatMessage(input, conversationId, caseId);
      setConversationId(response.conversationId);

      const aiMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: response.message,
      };

      setMessages((prev) => [...prev, aiMessage]);
    } catch (error) {
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: 'Sorry, I encountered an error. Please try again.',
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleSelectConversation = async (convId: string) => {
    try {
      setConversationId(convId);
      const history = await getConversationHistory(convId);
      const loadedMessages: Message[] = history.flatMap((item) => [
        {
          id: `user-${item.messageTimestamp}`,
          role: 'user' as const,
          content: item.userMessage,
        },
        {
          id: `assistant-${item.messageTimestamp}`,
          role: 'assistant' as const,
          content: item.botResponse,
        },
      ]);

      setMessages(
        loadedMessages.length > 0
          ? loadedMessages
          : [
              {
                id: '1',
                role: 'assistant',
                content: `Hello! I'm Lex, your AI legal assistant. How can I help you?`,
              },
            ]
      );
      setIsConversationsOpen(false);
    } catch (error) {
      console.error('Error loading conversation:', error);
    }
  };

  const handleNewConversation = () => {
    setConversationId(undefined);
    setMessages([
      {
        id: '1',
        role: 'assistant',
        content: `Hello! I'm Lex, your AI legal assistant. How can I help you?`,
      },
    ]);
    setIsConversationsOpen(false);
  };

  const toggleChat = () => {
    setIsChatOpen(!isChatOpen);
  };

  return {
    // State
    messages,
    input,
    isLoading,
    conversationId,
    isChatOpen,
    isConversationsOpen,
    messagesEndRef,
    // Setters
    setInput,
    setIsConversationsOpen,
    // Handlers
    handleSend,
    handleKeyPress,
    handleSelectConversation,
    handleNewConversation,
    toggleChat,
  };
};

